import React from 'react';
import { extractHeaderLabels, applyCellLabels } from './tableLabels';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { LibraryViewToggle } from '../ui/LibraryViewToggle';
import { useMaintenanceView } from './admin/MaintenanceView';

type ViewMode = 'list' | 'grid';

interface CardableTableProps {
  storageKey: string;
  children: React.ReactElement<HTMLTableElement>;
  className?: string;
  tableWrapperClassName?: string;
  tableWrapperStyle?: React.CSSProperties;
  heading?: React.ReactNode;
  defaultMode?: ViewMode;
  constrainToScrollParent?: boolean;
  /** When provided with onViewModeChange, view is controlled and stays in sync with toolbar. */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  showViewToggle?: boolean;
}

interface PreferenceController {
  get: <T>(path: string, fallback: T) => T;
  set: (path: string, value: unknown) => void;
  loaded: boolean;
}

/**
 * A table's List/Grid view at `tableViews.<storageKey>`: read on every render, saved only when the
 * person picks a view. Saving from an effect once preferences loaded stored the default for every
 * table someone merely opened (fixed 2026-10-02; docs/ui-preferences.md).
 */
export function useTableViewPreference(
  storageKey: string,
  defaultMode: ViewMode,
): [ViewMode, (mode: ViewMode) => void, PreferenceController['loaded']] {
  const { get, set, loaded } = useUserPrefs();
  const path = `tableViews.${storageKey}`;
  const mode = get<ViewMode>(path, defaultMode);
  const setMode = React.useCallback((next: ViewMode) => set(path, next), [path, set]);
  return [mode, setMode, loaded];
}

const OVERFLOW_PATTERN = /(auto|scroll|hidden|clip)/i;

function getVerticalClippingBounds(element: HTMLElement | null): { top: number; bottom: number } {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: Number.POSITIVE_INFINITY };
  }

  let topLimit = 0;
  let bottomLimit = window.innerHeight;
  let current: HTMLElement | null = element?.parentElement ?? null;

  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY || style.overflow;
    const overflowX = style.overflowX || style.overflow;

    if (OVERFLOW_PATTERN.test(overflowY) || OVERFLOW_PATTERN.test(overflowX)) {
      const rect = current.getBoundingClientRect();
      topLimit = Math.max(topLimit, rect.top);
      bottomLimit = Math.min(bottomLimit, rect.bottom);
    }

    current = current.parentElement;
  }

  return { top: topLimit, bottom: bottomLimit };
}

export default function CardableTable({
  storageKey,
  children,
  className,
  tableWrapperClassName,
  tableWrapperStyle,
  heading,
  defaultMode = 'list',
  constrainToScrollParent = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  showViewToggle = true,
}: CardableTableProps) {
  const isDirectory = useMaintenanceView() === 'directory';
  const [internalViewMode, setInternalViewMode, loaded] = useTableViewPreference(storageKey, defaultMode);
  const isControlled = controlledViewMode !== undefined && onViewModeChange != null;
  const viewMode = isDirectory ? 'list' : isControlled ? controlledViewMode : internalViewMode;
  const setViewMode = React.useCallback(
    (mode: ViewMode) => {
      if (isControlled) {
        onViewModeChange?.(mode);
      } else {
        setInternalViewMode(mode);
      }
    },
    [isControlled, onViewModeChange, setInternalViewMode]
  );
  const tableRef = React.useRef<HTMLTableElement | null>(null);
  const controlsRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [controlsHeight, setControlsHeight] = React.useState(0);
  const [constrainedHeight, setConstrainedHeight] = React.useState<number | null>(null);
  const rafRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const labels = extractHeaderLabels(table);
    if (!labels.length) return;
    applyCellLabels(table, labels);
  }, [children]);

  React.useLayoutEffect(() => {
    const node = controlsRef.current;
    if (!node) return;
    const measure = () => {
      setControlsHeight(node.offsetHeight);
    };
    measure();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => measure());
      observer.observe(node);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', measure);
      }
    };
  }, []);

  const tableElement = React.useMemo(() => {
    if (!React.isValidElement(children)) return null;
    const element = children as React.ReactElement<any>;
    return React.cloneElement(element, {
      ref: tableRef as React.Ref<HTMLTableElement>,
      'data-card-view': viewMode === 'grid' ? 'true' : undefined,
    });
  }, [children, viewMode]);

  const measureConstrainedHeight = React.useCallback(() => {
    if (!constrainToScrollParent) return;
    if (typeof window === 'undefined') return;
    const node = wrapperRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const { bottom } = getVerticalClippingBounds(node);
    const available = bottom - rect.top - 8;
    const next = Number.isFinite(available) && available > 0 ? available : null;
    setConstrainedHeight((prev) => (prev === next ? prev : next));
  }, [constrainToScrollParent]);

  const scheduleConstrainedMeasure = React.useCallback(() => {
    if (!constrainToScrollParent) return;
    if (typeof window === 'undefined') return;
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      measureConstrainedHeight();
    });
  }, [constrainToScrollParent, measureConstrainedHeight]);

  React.useLayoutEffect(() => {
    if (!constrainToScrollParent) return;
    scheduleConstrainedMeasure();
  }, [constrainToScrollParent, scheduleConstrainedMeasure, tableElement, viewMode, controlsHeight]);

  React.useEffect(() => {
    if (!constrainToScrollParent) {
      setConstrainedHeight(null);
    }
  }, [constrainToScrollParent]);

  React.useEffect(() => {
    if (!constrainToScrollParent) return;
    if (typeof window === 'undefined') return;
    const node = wrapperRef.current;
    if (!node) return;

    const handleSchedule = () => {
      scheduleConstrainedMeasure();
    };

    const ancestors: HTMLElement[] = [];
    let current: HTMLElement | null = node.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY || style.overflow;
      const overflowX = style.overflowX || style.overflow;
      if (OVERFLOW_PATTERN.test(overflowY) || OVERFLOW_PATTERN.test(overflowX)) {
        current.addEventListener('scroll', handleSchedule, { passive: true });
        ancestors.push(current);
      }
      current = current.parentElement;
    }

    window.addEventListener('resize', handleSchedule);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => scheduleConstrainedMeasure())
      : null;
    if (resizeObserver) {
      resizeObserver.observe(node);
      const tableNode = tableRef.current;
      if (tableNode) {
        resizeObserver.observe(tableNode);
      }
    }

    scheduleConstrainedMeasure();

    return () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      ancestors.forEach((ancestor) => ancestor.removeEventListener('scroll', handleSchedule));
      window.removeEventListener('resize', handleSchedule);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [constrainToScrollParent, scheduleConstrainedMeasure, tableElement]);

  const handleSetView = React.useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, [setViewMode]);

  const rootClass = ['cardable-table', className].filter(Boolean).join(' ');
  const rootStyle = React.useMemo(() => ({
    '--cardable-controls-height': `${Math.max(0, controlsHeight)}px`,
    '--table-controls-height': `${Math.max(0, controlsHeight)}px`,
  }) as React.CSSProperties, [controlsHeight]);
  const wrapperClass = ['cardable-table__scroll', tableWrapperClassName].filter(Boolean).join(' ');
  const controlsClass = ['cardable-table__controls', 'table-controls'].join(' ');

  const wrapperStyle = React.useMemo(() => {
    const base: React.CSSProperties = tableWrapperStyle ? { ...tableWrapperStyle } : {};
    if (constrainToScrollParent && constrainedHeight != null) {
      const nextMax = `${Math.floor(constrainedHeight)}px`;
      if (base.maxHeight != null) {
        const existing = typeof base.maxHeight === 'number' ? `${base.maxHeight}px` : String(base.maxHeight);
        base.maxHeight = `min(${existing}, ${nextMax})`;
      } else {
        base.maxHeight = nextMax;
      }
      if (base.overflowY == null && base.overflow == null) {
        base.overflowY = 'auto';
      }
      if (base.overscrollBehavior == null) {
        base.overscrollBehavior = 'contain';
      }
      if ((base.scrollbarGutter as any) == null) {
        (base as any).scrollbarGutter = 'stable both-edges';
      }
    }
    return base;
  }, [tableWrapperStyle, constrainToScrollParent, constrainedHeight]);

  const showControls = heading != null || (showViewToggle && !isDirectory);

  return (
    <div className={rootClass} data-view={viewMode} data-loaded={loaded ? 'true' : 'false'} style={rootStyle}>
      {showControls ? (
        <div ref={controlsRef} className={controlsClass}>
          {heading ? <span className="cardable-table__label">{heading}</span> : null}
          {showViewToggle && !isDirectory ? (
            <LibraryViewToggle
              value={viewMode}
              options={[
                { value: 'list', label: 'List' },
                { value: 'grid', label: 'Grid' },
              ]}
              onChange={handleSetView}
              ariaLabel="Change table view"
              className="cardable-table__toggle"
            />
          ) : null}
        </div>
      ) : (
        <div ref={controlsRef} className={controlsClass} style={{ display: 'none' }} aria-hidden="true" />
      )}
      <div ref={wrapperRef} className={wrapperClass} style={wrapperStyle}>
        {tableElement}
      </div>
    </div>
  );
}
